import { useEffect, useState } from "react";
import { Plus, Trash2, Mail, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { inputCls } from "./shared";

export function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role_key: "viewer", send_invite: false });
  const [resetFor, setResetFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/roles").then((r) => setRoles(r.data)).catch(() => {});
  }, []);

  const roleName = (key) => roles.find((r) => r.key === key)?.name || (key ? key.replace(/_/g, " ") : "Viewer");

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, email: form.email, role_key: form.role_key, send_invite: form.send_invite };
      if (!form.send_invite) payload.password = form.password;
      const { data } = await api.post("/admin/staff", payload);
      toast.success(data.invited ? "Invite email sent — they'll set their own password" : "Staff account created");
      setForm({ name: "", email: "", password: "", role_key: "viewer", send_invite: false });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const update = async (s, patch, msg) => {
    try {
      await api.put(`/admin/staff/${s.id}`, patch);
      if (msg) toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    await update(resetFor, { password: newPassword }, "Password updated");
    setResetFor(null);
    setNewPassword("");
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove staff access for ${s.email}?`)) return;
    await api.delete(`/admin/staff/${s.id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  return (
    <div className="max-w-3xl">
      <p className="vv-overline mb-4">Add Staff Member</p>
      <p className="text-[11px] text-vv-muted mb-4">Assign a role to control exactly what they can see and change. Either set a password yourself, or email them a secure setup link.</p>
      <form data-testid="staff-form" onSubmit={add} className="space-y-3 mb-10">
        <div className="grid md:grid-cols-4 gap-3">
          <input data-testid="staff-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={inputCls} />
          <input data-testid="staff-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
          <select data-testid="staff-role" value={form.role_key} onChange={(e) => setForm({ ...form, role_key: e.target.value })} className={inputCls}>
            {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </select>
          {!form.send_invite && (
            <input data-testid="staff-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className={inputCls} />
          )}
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-3 text-sm">
            <input data-testid="staff-invite-toggle" type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} className="accent-[#111111]" />
            <span className="flex items-center gap-2"><Mail size={13} strokeWidth={1.5} /> Email an invite link instead (they set their own password)</span>
          </label>
          <button data-testid="staff-add-button" type="submit" className="vv-btn-dark !px-5"><Plus size={12} /> Add Staff</button>
        </div>
      </form>

      <div className="space-y-3">
        {staff.map((s) => (
          <div key={s.id} data-testid={`staff-row-${s.email}`} className="border border-vv-black/10 p-4 flex flex-wrap items-center gap-4 justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {s.name}
                <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 ml-2 ${s.role === "admin" ? "bg-vv-black text-vv-ivory" : "bg-vv-stone/30"}`}>
                  {s.role === "admin" ? "Owner" : roleName(s.role_key)}
                </span>
                {s.status === "inactive" && <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 ml-1 bg-vv-burgundy text-vv-ivory">Inactive</span>}
              </p>
              <p className="text-[11px] text-vv-muted mt-1">{s.email}</p>
            </div>
            {s.role === "staff" && (
              <div className="flex items-center gap-2 flex-wrap">
                <select data-testid={`staff-role-select-${s.email}`} value={s.role_key || "viewer"} onChange={(e) => update(s, { role_key: e.target.value }, "Role updated — takes effect immediately")}
                  className="bg-transparent border border-vv-black/20 text-[11px] uppercase tracking-[0.15em] px-2 py-1.5 outline-none">
                  {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                </select>
                <button data-testid={`staff-status-${s.email}`} onClick={() => update(s, { status: s.status === "inactive" ? "active" : "inactive" }, s.status === "inactive" ? "Account reactivated" : "Account deactivated — access revoked")}
                  className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${s.status === "inactive" ? "border-vv-burgundy text-vv-burgundy" : "border-vv-forest text-vv-forest"}`}>
                  {s.status === "inactive" ? "Activate" : "Deactivate"}
                </button>
                <button data-testid={`staff-reset-${s.email}`} onClick={() => { setResetFor(s); setNewPassword(""); }} className="p-2 text-vv-muted hover:text-vv-black transition-colors" aria-label="Reset password"><KeyRound size={14} strokeWidth={1.5} /></button>
                <button data-testid={`staff-delete-${s.email}`} onClick={() => remove(s)} aria-label="Remove staff" className="p-2 text-vv-muted hover:text-vv-burgundy transition-colors"><Trash2 size={15} strokeWidth={1.5} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {resetFor && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-vv-black/40" onClick={() => setResetFor(null)} />
          <form data-testid="staff-reset-form" onSubmit={resetPassword} className="relative bg-white w-full max-w-sm p-8 space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-display text-xl">Reset Password</p>
              <button type="button" onClick={() => setResetFor(null)} aria-label="Close"><X size={18} strokeWidth={1.25} /></button>
            </div>
            <p className="text-[11px] text-vv-muted">Set a new password for {resetFor.email}. Share it with them privately.</p>
            <input data-testid="staff-reset-password" required type="password" minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 6 characters)" className={inputCls} />
            <button data-testid="staff-reset-save" type="submit" className="vv-btn-dark w-full">Update Password</button>
          </form>
        </div>
      )}
    </div>
  );
}
